<template>
  <Menu v-if="actions.length" as="div" class="relative inline-block text-left !ml-2">
    <MenuButton v-if="!iconButton" :class="{ 'bg-dark-brand-blue p-0 hover:bg-dark-brand-blue ': boxBackground }" class="p-2 !ml-0">
      <component :is="icon"
                 v-if="icon"
                 :color="boxBackground ? 'white' : '#2DA7D7'"
                 class="w-4 h-4 ml-1 mr-1"
                 aria-hidden="true" />
    </MenuButton>
    <MenuButton
      v-else
      :style="{ backgroundColor: $store.state.ui_options?.theme?.button_color }"
      :class="{ 'bg-dark-brand-blue p-0 hover:bg-dark-brand-blue': boxBackground }"
      class="p-2 border-0 cancel-btn !ml-0">
      <component :is="icon" :color="boxBackground ? 'white' : '#2DA7D7'" class="w-4 h-4 mt-1 ml-1 mr-1" aria-hidden="true" />
    </MenuButton>
    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0">
      <MenuItems
        v-if="!disabled"
        as="ul"
        class="absolute right-0 z-50 w-56 mt-2 origin-top-right bg-white border rounded-lg shadow-lg border-light-divider-border dark:border-dark-divider-border dark:bg-dark-screen-background">
        <MenuItem v-for="item in actions" :key="item.name" v-slot="{ active }" as="li">
          <div v-if="item?.divider" class="divide-y divide-gray-100" />
          <button
            v-else
            :disabled="item.disabled"
            :class="[
              active ? 'bg-light-brand-blue dark:bg-dark-brand-blue text-dark-body-text' : 'text-light-body-text dark:text-dark-body-text',
              'w-full group sm:text-sm flex items-center px-4 py-2  disabled:dark:bg-dark-transparent-brand-blue disabled:cursor-default disabled:opacity-60 transition ease-in-out duration-side-menu-transition-duration'
            ]"
            @click="item.action">
            <component
              :is="item.icon"
              v-if="item.icon"
              :color="`${active ? 'white' : '#899399'}`"
              :class="active ? 'text-dark-body-text' : 'text-common-grey'"
              class="w-5 h-5 mr-3 transition ease-in-out duration-side-menu-transition-duration"
              aria-hidden="true" />
            {{ item.name }}
          </button>
        </MenuItem>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script>
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import {
  Plus,
  Trash2,
  User,
  Users,
  ClipboardList,
  Mail,
  ChartColumnIncreasing,
  BookText,
  Folder,
  TrendingUp,
  MessagesSquare,
  FileDown,
  EyeOff,
  Eye,
  Lightbulb,
  Pencil,
  ArrowLeftRight,
  List,
  Tag,
  ClipboardPaste
} from "lucide-vue-next"
import { Tab, TabGroup, TabList, TabPanels } from "@headlessui/vue"
import { VIEW_TYPES } from "@/store/constants/references"
import { utils } from "@/store/helpers"
import QuickActionsIcon from "@/components/shared/icons/QuickActionsIcon.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Plus,
    Pencil,
    ArrowLeftRight,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    TabGroup,
    TabList,
    Tab,
    TabPanels,
    Trash2,
    Mail,
    FileDown,
    Users,
    List,
    Tag,
    MessagesSquare,
    EyeOff,
    Eye
  },
  props: {
    count: null,
    boxBackground: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    selectedRecords: {
      type: Array,
      default: () => []
    },
    excludedRecords: {
      type: Array,
      default: () => []
    },
    isSelectAll: {
      type: Boolean,
      default: false
    },
    selectedFullRecords: {
      type: Array,
      default: () => []
    },
    record: null,
    currentFilter: null,
    icon: {
      type: [Object, Function],
      default: () => QuickActionsIcon
    },
    iconButton: {
      type: Boolean,
      required: false
    },
    pageReference: null,
    objectType: null,
    componentReference: {
      type: String,
      required: false
    }
  },
  computed: {
    actions() {
      //TO DO: need to check and simplify this logic
      if (this.pageReference == "forecasting") {
        return [
          {
            name: this.$t("Hide Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.HIDE_COMPANIES_FROM_FORECAST)
            },
            disabled: this.selectedRecords.length == 0,
            icon: EyeOff
          },
          {
            name: this.$t("Show hidden records"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.SHOW_HIDDEN_COMPANIES_FROM_FORECAST)
            },
            disabled: false,
            icon: Eye
          },
          {
            name: this.$t("Delete Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.DELETE_COMPANIES_FROM_FORECAST)
            },
            disabled: this.selectedRecords.length == 0,
            icon: Trash2
          },
          {
            name: this.$t("Assign Tag"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ASSIGN_TAGS, {
                full_records: this.selectedFullRecords,
                records: this.selectedRecords,
                record: this.record,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Tag
          },
          {
            name: this.$t("Export to CSV"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.EXPORT_TABLE_TO_CSV, {
                component_reference: this.componentReference,
                records: this.selectedRecords,
                record: this.record,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            icon: FileDown
          }
        ]
        //This is for custom objects differ from "company, opportunity and contact" in portfolio and dashboard pages
      } else if (
        (this.pageReference == "portfolio" || this.pageReference == "dashboard") &&
        !(this.objectType == "company" || this.objectType == "opportunity" || this.objectType == "contact")
      ) {
        return [
          {
            name: this.$t("Add") + ` ${utils.getObjectTypeByType(this.objectType)?.label}`,
            action: () => {
              const metrics_obj = {
                object_type: this.objectType,
                page_reference: this.pageReference,
                view_type: VIEW_TYPES.FORM_CREATE
              }
              if (this.$route.params?.page) {
                metrics_obj["layout_page_reference"] = this.$route.params?.page
              }
              if (this.componentReference) {
                metrics_obj["component_reference"] = this.componentReference
              }

              this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
            },
            icon: Plus
          },

          {
            name: this.$t("Assign Tag"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ASSIGN_TAGS, {
                full_records: this.selectedFullRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                records: this.selectedRecords,
                record: this.record,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Tag
          },

          {
            name: this.$t("Bulk Metric Update"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.BULK_RECORDS_METRIC_UPDATE, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 || this.isSelectAll,
            icon: Pencil
          },
          {
            name: this.$t("Merge Metrics"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.MERGE_RECORDS_METRICS, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 || this.isSelectAll,
            icon: ArrowLeftRight
          },
          {
            name: this.$t("Export to CSV"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.EXPORT_TABLE_TO_CSV, {
                component_reference: this.componentReference,
                records: this.selectedRecords,
                record: this.record,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            icon: FileDown
          },
          {
            divider: true
          },
          {
            name: this.$t("Delete Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.DELETE_SELECTED_RECORDS, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Trash2
          }
        ]
      } else if (this.pageReference == "dashboard_leads_table") {
        return [
          {
            name: this.$t("Send Email"),
            action: () => {
              this.sendEmail()
            },
            icon: Mail
          },
          // {
          //   name: "Generate NPS",
          //   action: () => {
          //     this.$emitter.emit(ALL_EMITTERS.GENERATE_EXTERNAL_SCORE, {
          //       full_records: this.selectedFullRecords,
          //       excluded_records: this.excludedRecords,
          //       count: this.count,
          //       object_type: this.objectType,
          //       is_all_records: this.isSelectAll,
          //       records: this.selectedRecords
          //     })
          //   },
          //   disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
          //   icon: FileDown
          // },
          {
            name: this.$t("Export to CSV"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.EXPORT_LEADS_TABLE_CSV, this.componentReference)
            },
            disabled: this.selectedRecords.length == 0,
            icon: FileDown
          },
          {
            divider: true
          },
          {
            name: this.$t("Delete Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.DELETE_SELECTED_RECORDS, {
                is_leads_records: true,
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Trash2
          },
          {
            name: this.$t("View Growth Matrix"),
            action: () => {
              this.$router.push(`/organization?tab=growth`)
            },
            disabled: false,
            icon: TrendingUp
          },
          {
            name: this.$t("View Suggested Lists"),
            action: () => {
              this.$router.push(`/organization?tab=suggested`)
            },
            disabled: false,
            icon: Lightbulb
          }
        ]
      } else if (this.pageReference === "prospecting") {
        return [
          {
            name: this.$t("Create a Graph"),
            action: () => {
              // this.new_graph_modal_is_open = true

              this.$emitter.emit(ALL_EMITTERS.CREATE_GRAPH, {
                filter: this.currentFilter
              })
            },
            disabled: false,
            icon: ChartColumnIncreasing
          },
          {
            name: this.$t("Set Goal"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,

                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: TrendingUp
          },
          {
            name: this.$t("Assign Task"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: ClipboardList
          },
          {
            name: this.$t("Add New Interaction"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: MessagesSquare
          },
          {
            name: this.$t("Add New Plan"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: List
          },
          {
            name: this.$t("Assign Owner"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_OWNER, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0,
            icon: User
          },
          {
            name: this.$t("Assign Co-owners"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CHANGE_CO_OWNERS, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0,
            icon: Users
          },

          {
            name: this.$t("Add to List"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ADD_TO_EXPANSION_LIST, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: BookText
          },

          {
            name: this.$t("Send Email"),
            action: () => {
              this.sendEmail()
            },
            icon: Mail
          },
          {
            divider: true
          },

          {
            name: this.$t("Bulk Metric Update"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.BULK_RECORDS_METRIC_UPDATE, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 || this.isSelectAll,
            icon: Pencil
          },
          {
            name: this.$t("Merge Metrics"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.MERGE_RECORDS_METRICS, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 || this.isSelectAll,
            icon: ArrowLeftRight
          },

          {
            name: this.$t("Export to CSV"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.EXPORT_TABLE_TO_CSV, {
                component_reference: this.componentReference,
                records: this.selectedRecords,
                record: this.record,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            icon: FileDown
          },
          {
            name: this.$t("Delete Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.DELETE_SELECTED_RECORDS, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Trash2
          }
        ]
      } else if (this.pageReference === "prospecting_expansion_lists") {
        return [
          {
            name: this.$t("Create a Group"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_GROUP, {
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: this.currentFilter == null || this.currentFilter.length == 0,
            icon: Folder
          },
          {
            name: this.$t("Create a Graph"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_GRAPH, {
                filter: this.currentFilter,
                page_reference: this.pageReference,
                object_type: this.objectType
              })
            },
            disabled: false,
            icon: ChartColumnIncreasing
          },
          {
            name: this.$t("Set Goal"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: TrendingUp
          },
          {
            name: this.$t("Assign Task"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: ClipboardList
          },
          {
            name: this.$t("Add to List"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ADD_TO_EXPANSION_LIST, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: BookText
          },
          {
            name: this.$t("Add New Interaction"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: MessagesSquare
          },
          {
            name: this.$t("Add New Plan"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: List
          },
          {
            name: this.$t("Assign Owner"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_OWNER, {
                full_records: this.selectedFullRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                records: this.selectedRecords,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0,
            icon: User
          },
          {
            name: this.$t("Assign Co-owners"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CHANGE_CO_OWNERS, {
                full_records: this.selectedFullRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                records: this.selectedRecords,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0,
            icon: Users
          },
          {
            name: this.$t("Assign Tag"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ASSIGN_TAGS, {
                full_records: this.selectedFullRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                records: this.selectedRecords,
                record: this.record,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Tag
          },
          {
            name: this.$t("Remove from List"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.REMOVE_FROM_EXPANSION_LIST, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: BookText
          },
          {
            name: this.$t("Send Email"),
            action: () => {
              this.sendEmail()
            },
            icon: Mail
          },
          {
            name: this.$t("Bulk Metric Update"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.BULK_RECORDS_METRIC_UPDATE, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 || this.isSelectAll,
            icon: Pencil
          },
          {
            name: this.$t("Merge Metrics"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.MERGE_RECORDS_METRICS, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 || this.isSelectAll,
            icon: ArrowLeftRight
          },
          {
            name: this.$t("Export to CSV"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.EXPORT_TABLE_TO_CSV, {
                component_reference: this.componentReference,
                records: this.selectedRecords,
                record: this.record,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            icon: FileDown
          },
          {
            name: this.$t("Delete Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.DELETE_SELECTED_RECORDS, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Trash2
          }
        ]
      } else if (this.objectType == "company" && this.pageReference != "company_profile") {
        return [
          {
            name: this.$t("Add") + ` ${utils.getObjectTypeByType(this.objectType)?.label}`,
            action: () => {
              const metrics_obj = {
                object_type: this.objectType,
                page_reference: this.pageReference,
                view_type: VIEW_TYPES.FORM_CREATE
              }
              if (this.$route.params?.page) {
                metrics_obj["layout_page_reference"] = this.$route.params?.page
              }
              if (this.componentReference) {
                metrics_obj["component_reference"] = this.componentReference
              }
              this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
            },
            icon: Plus
          },

          {
            name: this.$t("Create a Group"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_GROUP, {
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: false,
            icon: Folder
          },
          {
            name: this.$t("Create a Graph"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_GRAPH, {
                filter: this.currentFilter,
                page_reference: this.pageReference,
                object_type: this.objectType
              })
            },
            disabled: false,
            icon: ChartColumnIncreasing
          },
          {
            name: this.$t("Set Goal"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: TrendingUp
          },
          {
            name: this.$t("Assign Task"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: ClipboardList
          },
          {
            name: this.$t("Add New Interaction"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: MessagesSquare
          },
          {
            name: this.$t("Add New Plan"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: List
          },
          {
            name: this.$t("Assign Owner"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_OWNER, {
                full_records: this.selectedFullRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                records: this.selectedRecords,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: User
          },
          {
            name: this.$t("Assign Co-owners"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CHANGE_CO_OWNERS, {
                full_records: this.selectedFullRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                records: this.selectedRecords,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Users
          },
          {
            name: this.$t("Assign Tag"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ASSIGN_TAGS, {
                full_records: this.selectedFullRecords,
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Tag
          },
          {
            name: this.$t("Add to List"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ADD_TO_EXPANSION_LIST, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: BookText
          },
          {
            name: this.$t("Send Email"),
            action: () => {
              this.sendEmail()
            },
            icon: Mail
          },
          {
            name: this.$t("Bulk Metric Update"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.BULK_RECORDS_METRIC_UPDATE, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Pencil
          },
          {
            name: this.$t("Merge Metrics"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.MERGE_RECORDS_METRICS, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: ArrowLeftRight
          },
          {
            name: this.$t("Export to CSV"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.EXPORT_TABLE_TO_CSV, {
                component_reference: this.componentReference,
                records: this.selectedRecords,
                record: this.record,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            icon: FileDown
          },
          {
            name: this.$t("Delete Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.DELETE_SELECTED_RECORDS, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Trash2
          }
        ]
      } else if (this.objectType == "contact") {
        return [
          {
            name: this.$t("Add") + ` ${utils.getObjectTypeByType(this.objectType)?.label}`,
            action: () => {
              const metrics_obj = {
                object_type: this.objectType,
                page_reference: this.pageReference,
                view_type: VIEW_TYPES.FORM_CREATE
              }
              if (this.$route.params?.page) {
                metrics_obj["layout_page_reference"] = this.$route.params?.page
              }
              if (this.componentReference) {
                metrics_obj["component_reference"] = this.componentReference
              }
              this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
            },
            icon: Plus
          },
          {
            name: this.$t("Send Email"),
            action: () => {
              this.sendEmail()
            },
            icon: Mail
          },
          // {
          //   name: "Generate NPS",
          //   action: () => {
          //     this.$emitter.emit(ALL_EMITTERS.GENERATE_EXTERNAL_SCORE, {
          //       full_records: this.selectedFullRecords,
          //       records: this.selectedRecords,
          //       object_type: this.objectType,
          //       excluded_records: this.excludedRecords,
          //       count: this.count,
          //       is_all_records: this.isSelectAll
          //     })
          //   },
          //   disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
          //   icon: FileDown
          // },
          {
            name: this.$t("Send a Survey"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.SELECT_A_SURVEY, this.selectedRecords)
            },
            disabled: !this.selectedRecords.length,
            icon: ClipboardPaste
          },
          {
            name: this.$t("Export to CSV"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.EXPORT_TABLE_TO_CSV, {
                component_reference: this.componentReference,
                records: this.selectedRecords,
                record: this.record,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            icon: FileDown
          },
          {
            divider: true
          },
          {
            name: this.$t("Bulk Metric Update"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.BULK_RECORDS_METRIC_UPDATE, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: !this.isSelectAll && this.selectedRecords.length == 0,
            icon: Pencil
          },
          {
            name: this.$t("Delete Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.DELETE_SELECTED_RECORDS, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Trash2
          },
          {
            name: this.$t("Add to List"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ADD_TO_EXPANSION_LIST, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: BookText
          },
          {
            name: this.$t("Assign Tag"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ASSIGN_TAGS, {
                full_records: this.selectedFullRecords,
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Tag
          }
        ]
      } else if (this.objectType == "opportunity" && this.pageReference != "opportunity_profile") {
        return [
          {
            name: this.$t("Add") + ` ${utils.getObjectTypeByType(this.objectType)?.label}`,
            action: () => {
              const metrics_obj = {
                object_type: this.objectType,
                page_reference: this.pageReference,
                view_type: VIEW_TYPES.FORM_CREATE
              }
              if (this.$route.params?.page) {
                metrics_obj["layout_page_reference"] = this.$route.params?.page
              }
              if (this.componentReference) {
                metrics_obj["component_reference"] = this.componentReference
              }
              this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
            },
            icon: Plus
          },
          {
            name: this.$t("Create a Group"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_GROUP, {
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: false,
            icon: Folder
          },

          {
            name: this.$t("Set Goal"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: TrendingUp
          },

          {
            name: this.$t("Assign Task"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: ClipboardList
          },
          {
            name: this.$t("Add New Interaction"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: MessagesSquare
          },
          {
            name: this.$t("Add New Plan"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: List
          },

          {
            name: this.$t("Add to List"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ADD_TO_EXPANSION_LIST, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: BookText
          },
          {
            name: this.$t("Assign Owner"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_OWNER, {
                full_records: this.selectedFullRecords,
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0,
            icon: User
          },
          {
            name: this.$t("Assign Co-owners"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CHANGE_CO_OWNERS, {
                full_records: this.selectedFullRecords,
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0,
            icon: Users
          },
          {
            name: this.$t("Assign Tag"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ASSIGN_TAGS, {
                full_records: this.selectedFullRecords,
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Tag
          },
          {
            divider: true
          },
          {
            name: this.$t("Send Email"),
            action: () => {
              this.sendEmail()
            },
            icon: Mail
          },
          {
            name: this.$t("Bulk Metric Update"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.BULK_RECORDS_METRIC_UPDATE, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 || this.isSelectAll,
            icon: Pencil
          },
          {
            name: this.$t("Merge Metrics"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.MERGE_RECORDS_METRICS, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 || this.isSelectAll,
            icon: ArrowLeftRight
          },
          {
            name: this.$t("Export to CSV"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.EXPORT_TABLE_TO_CSV, {
                component_reference: this.componentReference,
                records: this.selectedRecords,
                record: this.record,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            icon: FileDown
          },
          {
            name: this.$t("Delete Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.DELETE_SELECTED_RECORDS, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Trash2
          }
        ]
      } else if (this.pageReference == "company_profile" || this.pageReference == "opportunity_profile") {
        return [
          {
            name: this.$t("Add") + ` ${utils.getObjectTypeByType(this.objectType)?.label}`,
            action: () => {
              const metrics_obj = {
                object_type: this.objectType,
                page_reference: this.pageReference,
                view_type: VIEW_TYPES.FORM_CREATE
              }
              if (this.$route.params?.page) {
                metrics_obj["layout_page_reference"] = this.$route.params?.page
              }
              if (this.componentReference) {
                metrics_obj["component_reference"] = this.componentReference
              }
              this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
            },
            icon: Plus
          },
          {
            name: this.$t("Add New Goal"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_GOAL, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: TrendingUp
          },
          {
            name: this.$t("Add New Task"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_TASK, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: ClipboardList
          },
          {
            name: this.$t("Add New Interaction"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_INTERACTION, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: MessagesSquare
          },
          {
            name: this.$t("Add New Plan"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_PLAN, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            icon: List
          },
          {
            name: this.$t("Assign Owner"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CREATE_OWNER, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                object_type: this.objectType,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record
              })
            },
            disabled: this.selectedRecords.length == 0,
            icon: User
          },
          {
            name: this.$t("Change Co-Owners"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.CHANGE_CO_OWNERS, { record: this.record })
            },
            icon: User
          },
          {
            name: this.$t("Assign Tag"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ASSIGN_TAGS, {
                full_records: this.selectedFullRecords,
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Tag
          },
          {
            name: this.$t("Delete Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.DELETE_SELECTED_RECORDS, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Trash2
          },

          {
            name: this.$t("Add to List"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ADD_TO_EXPANSION_LIST, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record,
                object_type: this.objectType
              })
            },
            icon: ChartColumnIncreasing
          },

          // {
          //   name: "Generate NPS",
          //   action: () => {
          //     this.$emitter.emit(ALL_EMITTERS.GENERATE_EXTERNAL_SCORE, {
          //       full_records: this.selectedFullRecords,
          //       records: this.selectedRecords,
          //       excluded_records: this.excludedRecords,
          //       count: this.count,
          //       object_type: this.objectType,
          //       is_all_records: this.isSelectAll
          //     })
          //   },
          //   disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
          //   icon: FileDown
          // },
          {
            name: this.$t("Send Email"),
            action: () => {
              this.sendEmail()
            },
            icon: Mail
          }
        ]
      } else {
        return [
          {
            name: this.$t("Add") + ` ${utils.getObjectTypeByType(this.objectType)?.label}`,
            action: () => {
              const metrics_obj = {
                object_type: this.objectType,
                page_reference: this.pageReference,
                view_type: VIEW_TYPES.TABLE
              }
              if (this.$route.params?.page) {
                metrics_obj["layout_page_reference"] = this.$route.params?.page
              }
              if (this.componentReference) {
                metrics_obj["component_reference"] = this.componentReference
              }
              this.$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD, metrics_obj)
            },
            icon: Plus
          },
          {
            name: this.$t("Bulk Metric Update"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.BULK_RECORDS_METRIC_UPDATE, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 || this.isSelectAll,
            icon: Pencil
          },
          {
            name: this.$t("Merge Metrics"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.MERGE_RECORDS_METRICS, {
                records: this.selectedRecords,
                object_type: this.objectType,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter
              })
            },
            disabled: this.selectedRecords.length == 0 || this.isSelectAll,
            icon: ArrowLeftRight
          },

          {
            name: this.$t("Export to CSV"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.EXPORT_TABLE_TO_CSV, {
                component_reference: this.componentReference,
                records: this.selectedRecords,
                record: this.record,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            icon: FileDown
          },
          {
            divider: true
          },
          {
            name: this.$t("Delete Selected"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.DELETE_SELECTED_RECORDS, {
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Trash2
          },
          {
            name: this.$t("Assign Tag"),
            action: () => {
              this.$emitter.emit(ALL_EMITTERS.ASSIGN_TAGS, {
                full_records: this.selectedFullRecords,
                records: this.selectedRecords,
                excluded_records: this.excludedRecords,
                count: this.count,
                is_all_records: this.isSelectAll,
                filter: this.currentFilter,
                record: this.record,
                object_type: this.objectType
              })
            },
            disabled: this.selectedRecords.length == 0 && !this.isSelectAll,
            icon: Tag
          }
        ]
      }
    }
  },
  methods: {
    sendEmail() {
      this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
        records: this.selectedRecords,
        excluded_records: this.excludedRecords,
        count: this.count,
        is_all_records: this.isSelectAll,
        filter: this.currentFilter,
        record: this.record,
        object_type: this.objectType
      })
    }
  }
}
</script>
